/** List sharing v1 — the Share dialog behind the list header's button.
 *
 * Spec: `docs/specs/strategic-architecture/list-sharing-spec.md`. ONE access level, FULL: anyone this
 * dialog adds becomes functionally the list's owner — sees it, edits it, and can share it onward. The
 * dialog says so out loud, because "Share" in most products means "read-only", and here it does not.
 *
 * Three parts, top to bottom: (1) a colleague picker → chips; (2) Share, then the per-address
 * OUTCOMES; (3) the current-access table (owner first, then shares) with a ✕ per row and, for a
 * caller who is themselves a sharee, "Leave list".
 *
 * ⛔ **THE PICKER IS THE ROSTER, NOT THE DIRECTORY — this is a fixed live defect, do not revert it.**
 * v1 shipped reusing `useCrmLookup` (`POST /crm/lookup`), so the typeahead offered the ENTIRE CRM
 * contact directory: counterparties at BMO, Jefferies, IADB. Owner ruling 2026-08-27, verbatim: *"this
 * is for internal users only — the share feature should reference users with logins."* The source is
 * now `useShareCandidates` → `GET /crm/lists/{id}/share-candidates` (the active Ember roster), fetched
 * once per open and filtered locally — tens of rows, so no debounce and no request per keystroke.
 *
 * Typing a full address and pressing Enter still stages it (a colleague whose directory name you don't
 * know), because the SERVER is the authority, not the picker: `grant_shares` refuses a non-login
 * grantee with `not_internal_user` and writes nothing. Staging is deliberately NOT pre-refused against
 * the candidate list — the write gate accepts a superset (any internal login-holder, roster row or
 * not), and a UI predicate that refused more than the server would make a real colleague unshareable.
 *
 * **The access table renders from the QUERY, always** (`useListShares`). Both mutations write their
 * fresh table into that cache entry (see `useCrm`), so this component never reads `grant.data` for
 * state. Preferring the mutation response froze the table for the dialog's lifetime: a revoked
 * person stayed listed with a live ✕ that then 404'd as "please try again"
 * (adv-review [0]/[2]/[3]/[10]). The grant's per-address verdicts are held in local state for the
 * outcome LINES only, and cleared before the next action so stale verdicts never sit above a
 * changed table.
 *
 * Three outcomes are answers, not errors, and are worded that way:
 *   * `denied_by_ceiling` — the permission_class ceiling is absolute (`governance.grant_share`
 *     re-reads the class off the stored list row). Retrying cannot change it; someone has to be
 *     granted the class first. Never rendered as a failure the operator should retry.
 *   * `not_internal_user` — the internal-only ruling. Also final, and worded so nobody goes looking
 *     for a clearance to grant: no clearance makes an external person shareable.
 *   * `owner` — that address owns the list; their access comes from ownership, not a share row.
 *
 * Modal mechanics: the shared `modalBackdrop` card pattern, backdrop-mousedown close, and Escape
 * handled ON THE CARD with `stopPropagation` (the `CrmFactEditModal` pattern) — NOT a document-level
 * listener, which also closed the record panel / side dock underneath (adv-review [20]). Focus starts
 * in the typeahead (`autoFocus`, with no competing card-focus effect — one silently defeated the
 * other, adv-review [7]), so Escape from the field bubbles to the card handler. Because a card-level
 * handler only sees keys from INSIDE the card, focus must never be allowed to escape it: the card
 * itself is programmatically focusable (`tabIndex={-1}`) and is refocused after a revoke, whose row
 * unmount would otherwise drop focus to `<body>` and leave Escape dead (second-reader nit 1).
 */
import { useRef, useState } from 'react';
import styles from './crm.module.css';
import {
  useGrantListShares, useListShares, useRevokeListShare, useShareCandidates,
} from '../../hooks/useCrm';
import type { CrmShareGrantResult, CrmShareOutcome } from '../../types';

interface Props {
  listId: number;
  listName: string;
  onClose: () => void;
  /** Called after the caller removes THEMSELVES — the list is no longer theirs to read, so the host
   *  navigates away rather than leaving a grid on screen that the next fetch will 404. */
  onLeft?: () => void;
}

/** A staged recipient: the address is what the server grants on; the name is display only. */
interface Chip { email: string; name: string | null }

const OUTCOME_TEXT: Record<CrmShareOutcome, (r: CrmShareGrantResult) => string> = {
  granted: (r) => `${r.email} now has full access`,
  already_shared: (r) => `${r.email} already had access`,
  owner: (r) => `${r.email} owns this list — they already have full access`,
  denied_by_ceiling: (r) => `${r.email} can't be granted — their clearance doesn't include this list's class`,
  not_internal_user: (r) => `${r.email} isn't an Ember colleague with a login — lists can only be shared internally`,
  unknown_address: (r) => `${r.email} isn't a known address here`,
};

const _norm = (s: string) => s.trim().toLowerCase();
const _looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(_norm(s));

export function CrmShareDialog({ listId, listName, onClose, onLeft }: Props) {
  // Focus target of last resort. The card is programmatically focusable (`tabIndex={-1}`) but NOT
  // focused on mount — the typeahead's autoFocus wins that, deliberately. It is focused again after
  // a revoke, whose row unmount would otherwise drop focus to <body> and strand the Escape handler.
  const cardRef = useRef<HTMLDivElement>(null);
  const shares = useListShares(listId);
  const grant = useGrantListShares(listId);
  const revoke = useRevokeListShare(listId);
  const [chips, setChips] = useState<Chip[]>([]);
  const [q, setQ] = useState('');
  // The keyboard-highlighted colleague in the picker, by person id (see `activeIdx`).
  const [activeId, setActiveId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<CrmShareGrantResult[] | null>(null);
  // The roster, once per open — no debounce, no per-keystroke request: filtering happens below.
  const candidates = useShareCandidates(listId);

  // THE single source of truth — see the header note.
  const data = shares.data;
  const rows = data?.shares ?? [];
  const owner = data?.owner;
  const callerId = data?.caller_person_id;
  // The caller's own share row, EXCLUDING the owner: an owner can hold a redundant share row on
  // their own list (the A4-G importer grants to every address named, theirs included), and must
  // never be offered "Leave list" on a list they still own (adv-review [11]/[21]). The backend now
  // filters those rows out of the payload as well — this is the second answer, not the only one.
  const mine = rows.find((s) => s.person_id === callerId && s.person_id !== owner?.person_id) ?? null;
  // the owner's address counts as held too, so the typeahead marks them rather than offering them
  const held = new Set(
    [...rows.map((s) => s.email), owner?.email].map((e) => _norm(e ?? '')).filter(Boolean),
  );

  const addChip = (email: string, name: string | null) => {
    const e = _norm(email);
    if (!e || chips.some((c) => c.email === e)) return;
    setChips([...chips, { email: e, name }]);
    setQ('');
  };
  // The offer: the roster, minus anyone already staged, narrowed by what has been typed. Matching on
  // name AND address, because "who is dave" and "dave@" are the same question. With an empty box the
  // WHOLE roster shows — it is a picker of tens of colleagues, not a search of a 200k directory, and
  // making someone type before seeing their own team is friction with nothing behind it.
  const ql = _norm(q);
  const results = (candidates.data?.candidates ?? []).filter(
    (c) => !chips.some((x) => x.email === c.email)
      && (!ql || c.email.includes(ql) || (c.name ?? '').toLowerCase().includes(ql)),
  );
  // What ArrowDown/Enter may land on — a `held` row renders (marked) but cannot be picked, so
  // highlighting it would be a keystroke that does nothing.
  const pickable = results.filter((c) => !held.has(c.email));
  // The highlight is stored as a PERSON ID, not an index: typing filters the list under the cursor,
  // and an index would then silently point at somebody else (or out of range, which would need a
  // setState during render to clamp). A vanished id simply resolves to -1 = "nothing highlighted".
  const activeIdx = activeId === null
    ? -1 : pickable.findIndex((c) => c.person_id === activeId);
  const activeCandidate = activeIdx >= 0 ? pickable[activeIdx] : null;

  const doShare = () => {
    if (chips.length === 0) return;
    setErr(null);
    grant.mutate(chips.map((c) => c.email), {
      onSuccess: (out) => { setChips([]); setOutcomes(out.results); },
      onError: () => setErr('Could not share the list — please try again.'),
    });
  };
  const doRevoke = (shareId: number, isSelf: boolean) => {
    setErr(null);
    setOutcomes(null);   // the last Share's verdicts no longer describe the table below
    // Removing a row UNMOUNTS the ✕ that was focused, which drops focus to <body> — outside the
    // card, where the card-level Escape handler can no longer see keystrokes. So focus comes back to
    // the card (hence its tabIndex={-1}) once the mutation settles, either way.
    const refocus = () => cardRef.current?.focus();
    revoke.mutate(shareId, {
      onSuccess: (out) => {
        if (out.self) { onClose(); onLeft?.(); return; }
        refocus();
      },
      onError: () => {
        setErr(isSelf
          ? 'Could not leave the list — please try again.'
          : 'Could not remove that person — please try again.');
        refocus();
      },
    });
  };

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div
        ref={cardRef}
        tabIndex={-1}
        className={styles.shareModalCard}
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${listName}`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Escape closes THIS dialog and stops there — the grid's record panel and its side dock
          // each keep a document-level Escape listener, and one keystroke used to close both.
          if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
        }}
      >
        <h3 className={styles.modalTitle}>Share “{listName}”</h3>
        {/* Said plainly, once: this is not a read-only share. */}
        <div className={styles.hint}>
          Anyone you add gets the same access you have — they can view and edit this list, and share
          it with others. Only the owner can delete it. Ember colleagues only: external contacts in
          the CRM can’t be given a list.
        </div>

        {/* ── (1) who to add ── */}
        <div className={styles.refEditorChips}>
          {chips.length === 0 && <span className={styles.gridEmpty}>nobody selected yet</span>}
          {chips.map((c) => (
            <span key={c.email} className={styles.gridRef}>
              {c.name || c.email}
              <button
                type="button"
                className={styles.refEditorRemove}
                aria-label={`Remove ${c.name || c.email}`}
                onClick={() => setChips(chips.filter((x) => x.email !== c.email))}
              >×</button>
            </span>
          ))}
        </div>
        <input
          className={styles.refEditorSearch}
          autoFocus
          value={q}
          placeholder="Search colleagues, or type an Ember email address…"
          aria-label="Search people to share with"
          role="combobox"
          aria-expanded={pickable.length > 0}
          aria-controls="share-candidate-list"
          aria-activedescendant={
            activeCandidate ? `share-candidate-${activeCandidate.person_id}` : undefined}
          onChange={(e) => { setQ(e.target.value); setActiveId(null); }}
          onKeyDown={(e) => {
            // ⚠ ARROW KEYS, NOT TAB. The picker renders the whole roster on open, so leaving ~29
            // focusable rows between this field and the Share button made a keyboard user tab through
            // every colleague to reach it (adv-review 14). The rows are `tabIndex={-1}`; this field is
            // the single tab stop, and the active row is announced via `aria-activedescendant`.
            if (e.key === 'ArrowDown' && pickable.length > 0) {
              e.preventDefault();
              setActiveId(pickable[(activeIdx + 1) % pickable.length].person_id);
              return;
            }
            if (e.key === 'ArrowUp' && pickable.length > 0) {
              e.preventDefault();
              setActiveId(pickable[activeIdx <= 0 ? pickable.length - 1 : activeIdx - 1].person_id);
              return;
            }
            if (e.key !== 'Enter') return;
            // Enter takes the highlighted colleague if there is one, else stages a typed address — a
            // colleague whose directory name you don't know is still shareable, and the SERVER is the
            // authority (`not_internal_user` if they aren't a login). Deliberately not pre-checked
            // against the roster: the write gate accepts a superset of it, so refusing here would
            // make a real colleague unshareable.
            if (activeCandidate) {
              e.preventDefault();
              addChip(activeCandidate.email, activeCandidate.name);
              setActiveId(null);
              return;
            }
            if (_looksLikeEmail(q)) { e.preventDefault(); addChip(q, null); }
          }}
        />
        <div className={styles.refEditorResults} id="share-candidate-list" role="listbox"
             aria-label="Ember colleagues">
          {candidates.isPending && <span className={styles.gridEmpty}>Loading colleagues…</span>}
          {candidates.isError && (
            <span className={styles.gridEmpty}>
              Couldn’t load the colleague list — type a full Ember address and press Enter.
            </span>
          )}
          {!candidates.isPending && !candidates.isError && results.length === 0 && (
            <span className={styles.gridEmpty}>
              No colleague matches
              {_looksLikeEmail(q) ? ' — press Enter to use this address anyway.' : '.'}
            </span>
          )}
          {results.map((c) => (
            <button
              key={c.person_id}
              id={`share-candidate-${c.person_id}`}
              type="button"
              role="option"
              aria-selected={activeCandidate?.person_id === c.person_id}
              // NOT a tab stop — see the input's ArrowDown/ArrowUp handler. Still clickable, and
              // still reachable from the keyboard; what it is not is one of 29 stops before Share.
              tabIndex={-1}
              data-active={activeCandidate?.person_id === c.person_id ? 'true' : undefined}
              className={styles.refEditorResult}
              disabled={held.has(c.email)}
              onClick={() => { addChip(c.email, c.name); setActiveId(null); }}
              onMouseEnter={() => setActiveId(held.has(c.email) ? null : c.person_id)}
            >
              + {c.name || c.email}
              <span className={styles.refEditorDetail}>
                {' · '}{c.email}
                {held.has(c.email) ? ' · already has access' : ''}
                {/* Offered, not hidden: Share will answer `denied_by_ceiling` for them, and the fix
                    is a clearance grant — which nobody can act on if the person is invisible. */}
                {!held.has(c.email) && !c.within_ceiling ? ' · no clearance for this list' : ''}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.gridMiniBtn} onClick={onClose}>Close</button>
          <button
            type="button"
            className={styles.gridPageBtn}
            disabled={chips.length === 0 || grant.isPending}
            onClick={doShare}
          >{grant.isPending ? 'Sharing…' : 'Share'}</button>
        </div>

        {err && <div className={styles.modalError} role="alert">{err}</div>}

        {/* ── (2) per-address outcomes from the last Share ── */}
        {outcomes && (
          <ul className={styles.shareOutcomes} aria-label="Sharing results">
            {outcomes.map((r) => (
              <li key={r.email} data-outcome={r.outcome}>{OUTCOME_TEXT[r.outcome](r)}</li>
            ))}
          </ul>
        )}

        {/* ── (3) current access ── */}
        <div className={styles.groupLabel}>Who has access</div>
        {shares.isPending && <span className={styles.gridEmpty}>Loading…</span>}
        {shares.isError && (
          <div className={styles.modalError} role="alert">Could not load who has access.</div>
        )}
        {owner && (
          <div className={styles.shareRows}>
            <div className={styles.shareRow}>
              <span className={styles.shareRowName}>{owner.name || owner.email || 'Owner'}</span>
              <span className={styles.refEditorDetail}>{owner.email}</span>
              <span className={`${styles.chip} ${styles.chipMuted}`}>owner</span>
            </div>
            {rows.length === 0 && (
              <span className={styles.gridEmpty}>Not shared with anyone yet.</span>
            )}
            {rows.map((s) => {
              const isSelf = s.person_id === callerId && s.person_id !== owner.person_id;
              const label = s.name || s.email || 'Restricted';
              // effective === false: the row is live but axis-1 no longer admits its holder, so they
              // cannot open the list. Shown (and revocable) rather than hidden, with the reason.
              const inert = s.effective === false;
              // internal === false: a row the internal-only ruling would no longer grant (an
              // external, or a colleague who has left). Only shown when the row is otherwise LIVE —
              // for an inert row the clearance chip already says they cannot open the list, and two
              // chips saying "no access" for one row is noise, not information.
              const notInternal = s.internal === false && !inert;
              return (
                <div key={s.share_id} className={styles.shareRow}>
                  <span className={styles.shareRowName}>{label}</span>
                  <span className={styles.refEditorDetail}>
                    {s.email}
                    {s.granted_by ? ` · added by ${s.granted_by}` : ''}
                    {s.granted_at ? ` · ${s.granted_at.slice(0, 10)}` : ''}
                  </span>
                  {inert && (
                    <span
                      className={`${styles.chip} ${styles.chipDup}`}
                      title="Their clearance no longer covers this list's class, so they can't open it. Restore it in Admin → Governance → Clearances, or remove them here."
                    >no access — clearance withdrawn</span>
                  )}
                  {notInternal && (
                    <span
                      className={`${styles.chip} ${styles.chipDup}`}
                      title="Sharing is for Ember colleagues with a login. This access predates that rule, or they have since left the firm — it still works, so remove them here if it should not."
                    >not an Ember login — still has access</span>
                  )}
                  {isSelf && <span className={`${styles.chip} ${styles.chipMuted}`}>you</span>}
                  {/* The caller's OWN row carries no ✕ — leaving is the "Leave list" button below.
                      One affordance per action: two controls with the same accessible name is how a
                      screen-reader user ends up unable to tell them apart. */}
                  {!isSelf && (
                    <button
                      type="button"
                      className={styles.refEditorRemove}
                      disabled={revoke.isPending}
                      aria-label={`Remove ${label}`}
                      title={`Remove ${label}`}
                      onClick={() => doRevoke(s.share_id, false)}
                    >✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {mine && (
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.gridMiniBtn}
              disabled={revoke.isPending}
              onClick={() => doRevoke(mine.share_id, true)}
            >Leave list</button>
          </div>
        )}
      </div>
    </div>
  );
}
